import { useEffect, useMemo, useState } from "react";
import type { MeetingPrep, PatientDetail } from "@care-by-ella/contracts";
import { flagRuleLabel } from "@/lib/flag-rule-labels";
import { STATUS_TONE_CLASS } from "@/lib/status-tone";
import { FormResponses } from "@/components/FormResponses";
import { SummarySections } from "@/components/SummarySections";

// The whole health declaration, slid in over the right edge of the meeting
// screen so the doctor can read it without leaving the call. Two views:
// everything the patient answered (grouped as she saw it), or only what
// the evaluators flagged — the deviations, first, during the conversation.
// Flagged rows carry the rule's plain-Swedish label and the evaluator's
// detail; a kontraindikation is set in plommon djup, a flag in peach.

type View = "all" | "flagged";

export function HealthDeclarationPanel({
  prep,
  patient,
  open,
  onClose,
}: {
  prep: MeetingPrep;
  patient: PatientDetail | null;
  open: boolean;
  onClose: () => void;
}) {
  const [view, setView] = useState<View>("all");

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const intake = patient?.formSubmissions.find((s) => s.templateKey === "intake");
  const flagged = useMemo(() => {
    const rows: Array<{
      rule: string;
      kind: "hard_stop" | "flag";
      detail?: string;
    }> = [];
    const seen = new Set<string>();
    for (const h of intake?.hardStops ?? []) {
      seen.add(h.rule);
      rows.push({ rule: h.rule, kind: "hard_stop", detail: h.detail });
    }
    for (const f of intake?.flags ?? []) {
      seen.add(f.rule);
      rows.push({ rule: f.rule, kind: "flag", detail: f.detail });
    }
    for (const r of prep.riskFlags) {
      if (seen.has(r.rule)) continue;
      rows.push({ rule: r.rule, kind: r.kind, detail: r.detail });
    }
    return rows;
  }, [intake, prep.riskFlags]);

  const summary = prep.preConsultSummary;
  const submittedAt = prep.healthDeclaration.part1SubmittedAt;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40" role="presentation">
      <button
        type="button"
        aria-label="Stäng hälsodeklarationen"
        onClick={onClose}
        className="absolute inset-0 bg-brand-deep/30 backdrop-blur-[2px]"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="hd-title"
        className="absolute inset-y-0 right-0 flex w-full max-w-[560px] flex-col bg-background shadow-[-24px_0_48px_-24px_rgba(42,26,40,0.45)]"
      >
        <header className="flex items-start justify-between gap-md border-b border-surfaceMuted bg-surface px-lg py-md">
          <div>
            <h2
              id="hd-title"
              className="font-display text-[22px] leading-7 text-textPrimary"
            >
              Hälsodeklaration
            </h2>
            <p className="text-xs text-textSecondary">
              {submittedAt
                ? `Inskickad ${new Date(submittedAt).toLocaleDateString("sv-SE", { day: "numeric", month: "long", year: "numeric" })}${prep.healthDeclaration.part1Reviewed ? " · granskad" : ""}`
                : "Inte inskickad än"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-pill border border-surfaceMuted px-md py-[6px] text-sm text-textSecondary hover:text-textPrimary"
          >
            Stäng
          </button>
        </header>

        <div className="flex gap-xs border-b border-surfaceMuted bg-surface px-lg py-sm">
          {(
            [
              ["all", "Allt"],
              ["flagged", `Endast flaggat · ${flagged.length}`],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={view === key}
              onClick={() => setView(key)}
              className={`rounded-pill px-md py-[6px] text-sm font-semibold transition ${
                view === key
                  ? "bg-brand text-textOnBrand"
                  : "bg-brand-accent text-brand-deep hover:opacity-90"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-lg py-md">
          {view === "flagged" ? (
            flagged.length === 0 ? (
              <p className="text-sm text-textSecondary">
                Inget flaggat i hälsodeklarationen.
              </p>
            ) : (
              <ul className="flex flex-col gap-sm">
                {flagged.map((f) => (
                  <li
                    key={f.rule}
                    className="rounded-md bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06)]"
                  >
                    <div className="flex items-start gap-sm">
                      <span
                        className={`shrink-0 rounded-pill px-sm py-[3px] text-[11px] font-semibold uppercase tracking-wide ${
                          f.kind === "hard_stop"
                            ? "bg-notify text-white"
                            : STATUS_TONE_CLASS.risk
                        }`}
                      >
                        {f.kind === "hard_stop" ? "Stopp" : "Flagga"}
                      </span>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-deep">
                          {flagRuleLabel(f.rule)}
                        </p>
                        {f.detail ? (
                          <p className="text-sm text-textSecondary">{f.detail}</p>
                        ) : null}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            )
          ) : (
            <div className="flex flex-col gap-lg">
              {summary && summary.sections.length > 0 ? (
                <section className="rounded-md bg-surface p-md shadow-[0_1px_2px_rgba(42,26,40,0.06)]">
                  <SummarySections sections={summary.sections} />
                </section>
              ) : (
                <p className="text-sm text-textSecondary">
                  Ingen sammanfattning att visa
                  {submittedAt ? " — svaren finns under Formulärsvar nedan." : "."}
                </p>
              )}
              {patient ? (
                <FormResponses
                  submissions={patient.formSubmissions}
                  intakeSummary={summary}
                />
              ) : null}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}
